import { useUnmount, useMount } from '@apia/util';
import uniqueId from 'lodash-es/uniqueId';
import React from 'react';
import { getVariant, injectStyles, Box, BoxProps } from '@apia/theme';
import { FormContext, IFormContext } from './FormContext';
import { TValidationResult } from '../types';
import { validationsStore } from '../validationStore';
import { formStyles } from './formStyles';

export type TOnValidate<
  ValueType extends Record<string, unknown> = Record<string, unknown>,
  SubmitValueType extends Record<keyof ValueType, unknown> = ValueType,
> = (
  validationResult: TValidationResult<ValueType, SubmitValueType>,
) => unknown;

export interface IForm<
  ValueType extends Record<string, unknown>,
  SubmitValueType extends Record<keyof ValueType, unknown> = ValueType,
> {
  /**
   * Si se pasa en true, los campos que se registren en este form no
   * sobreescribirán valores previos si es que existen, sin importar el valor de
   * avoidRegisterIfExists pasado al useField.
   */
  avoidFieldsOverride?: boolean;
  /**
   * Impide que se apliquen los estilos que indican que un campo fue alterado.
   */
  avoidFieldsTouch?: boolean;
  children: React.ReactNode;
  className?: string;
  initialValues?: Partial<ValueType>;
  name?: string;
  onUnmount?: () => unknown;
  /**
   * Cuando se ejecuta una acción de validación desde
   * algún lugar de la aplicación se llama a este método
   * con el resultado de dicha ejecución.
   */
  onValidate?: TOnValidate<ValueType, SubmitValueType>;
  persistToSessionStorage?: boolean;
  /**
   * Permite definir si el formulario va a ser borrado de la validación al ser
   * desmontado
   */
  unregisterOnUnmount?: boolean;
}

/**
 * Cuando se desea usar el sistema de validación,
 * se debe utilizar este componente como form contenedor
 * de todos los campos que quieran validarse.
 *
 * Es importante que no se coloquen formularios dentro
 * de formularios ya que esto podría afectar negativamente
 * el proceso de validación.
 *
 * @props
 * *name:* Se puede pasar un nombre para identificar
 * el form. Este nombre será utilizado para almacenar
 * la información en Redux, por lo que si se desea
 * acceder a la información recolectada por la librería,
 * es conveniente pasar un nombre conocido y único. En
 * caso de no se pase ningún nombre, la aplicación
 * generará un nombre único automaticamente.
 *
 * *onValidate:* Un callback que será llamado cuando
 * se invoque el método validateForm del store o cuando
 * se lance el evento de submit del elemento form (por
 * ejemplo mediante un click en un botón que no tenga
 * type="button"), con el resultado de la validación.
 *
 * avoidFieldsOverride
 * avoidFieldsTouch
 *
 * @see useField
 * @see useForm
 *
 * @important Debido a que este hook renderiza un elemento
 * form en el DOM, es importante agregar el atributo
 * type="button" a todos los botones dentro del formulario
 * que no se desea que disparen la validación.
 *
 * @example
 * Para ver un ejemplo funcional, mira el componente
 * Invitations.tsx
 *
 * // IMPORTANTE: El tipado del componente Form es genérico
 * // por lo que es posible instanciarlo como se muestra a
 * // continuación, lo que nos da el tipado correcto en el
 * // callback onValidate.
 * <Form<FormDataType> onValidate={(res) => {
 *  if(hasSucceedValidation(res)) {
 *    // Do something with submitValues
 *  } else {
 *    // Do something with errors
 *  }
 * }}>
 *    ...{children}
 * </Form>
 */
const Form = <
  ValueType extends Record<string, unknown>,
  SubmitValueType extends Record<keyof ValueType, unknown> = ValueType,
>({
  avoidFieldsOverride,
  avoidFieldsTouch,
  children,
  className,
  name,
  initialValues,
  onUnmount,
  onValidate,
  persistToSessionStorage,
  unregisterOnUnmount,
}: IForm<ValueType, SubmitValueType>) => {
  const actualName = React.useMemo(
    () => name ?? `ValidationForm${uniqueId()}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSubmit = React.useCallback<React.FormEventHandler>(
    async (ev) => {
      ev.preventDefault();
      const validationRes = (await validationsStore.validateForm(
        actualName,
      )) as TValidationResult<ValueType, SubmitValueType>;
      if (validationRes && onValidate) onValidate(validationRes);
    },
    [actualName, onValidate],
  );
  const contextValue = React.useMemo<IFormContext>(
    () => ({
      avoidFieldsTouch,
      name: actualName,
      validate: async () => validationsStore.validateForm(actualName),
    }),
    [actualName, avoidFieldsTouch],
  );

  useUnmount(() => {
    onUnmount?.();
    if (unregisterOnUnmount !== false)
      validationsStore.unregisterForm(actualName);
  });

  const [hasRegistered, setHasRegistered] = React.useState(false);

  useMount(() => {
    validationsStore.registerForm(actualName, {
      avoidFieldsOverride: avoidFieldsOverride ?? initialValues !== undefined,
      initialValues,
      persistToSessionStorage,
    });
    setHasRegistered(true);
  });
  if (!hasRegistered) return null;

  return (
    <FormContext.Provider value={contextValue}>
      <Box
        as="form"
        {...getVariant('layout.common.validations.form')}
        className={`validationsForm ${className}`}
        id={`ValidationForm-${actualName}`}
        {...({ name: actualName } as unknown as BoxProps)}
        onSubmit={handleSubmit}
      >
        {children}
      </Box>
    </FormContext.Provider>
  );
};

injectStyles('layout.common.validations.form', formStyles);

export { Form };
